import React, { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { STATUS_META } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function cleanNames(text) {
  return [
    ...new Set(
      text
        .split(/[\n,]/)
        .map((s) => s.trim().toLowerCase().replace(/[^a-z0-9]/g, ""))
        .filter(Boolean)
    ),
  ].slice(0, 10);
}

export default function BulkSearch({ onOpenSingle }) {
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    const names = cleanNames(text);
    if (!names.length) return;
    setLoading(true);
    try {
      const results = await Promise.all(
        names.map(async (n) => {
          try {
            const res = await axios.get(`${API}/search`, { params: { q: n } });
            const list = res.data.results || [];
            const pick = list.find((r) => r.tld === "com") || list[0] || {};
            return {
              name: n,
              domain: pick.domain || `${n}.com`,
              status: pick.status || "maybe",
              live: !!pick.live,
            };
          } catch {
            return { name: n, domain: `${n}.com`, status: "maybe", live: false };
          }
        })
      );
      setRows(results);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        spellCheck={false}
        placeholder="Paste up to 10 names — one per line or comma-separated (e.g. mystartup, coolapp, myshop)…"
        className="w-full rounded-xl border border-white/10 bg-[#141417] p-4 text-white placeholder-zinc-600 outline-none transition-colors focus:border-emerald-400/60 focus:ring-2 focus:ring-emerald-400/20"
      />
      <button
        onClick={run}
        disabled={loading}
        className="mt-3 inline-flex items-center gap-2 rounded-md bg-emerald-500 px-5 py-2 text-sm font-semibold text-black transition-colors hover:bg-emerald-400 disabled:opacity-60"
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        Check availability
      </button>

      {rows.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-xl border border-white/10 bg-[#0e0e10]">
          {rows.map((r, i) => {
            const m = STATUS_META[r.status] || STATUS_META.maybe;
            return (
              <div
                key={r.domain}
                className={`flex items-center gap-4 px-5 py-3 ${
                  i ? "border-t border-white/5" : ""
                }`}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: m.color }}
                />
                <span className="flex-1 truncate font-mono text-white">{r.domain}</span>
                {r.live && (
                  <span className="text-[10px] uppercase tracking-wide text-emerald-400/80">
                    live
                  </span>
                )}
                <span className="text-sm font-medium" style={{ color: m.color }}>
                  {m.label}
                </span>
                <button
                  onClick={() => onOpenSingle(r.name)}
                  className="shrink-0 text-xs text-zinc-400 underline-offset-2 transition-colors hover:text-white hover:underline"
                >
                  view all TLDs
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
